import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { CloudText, Reveal, AltitudeMarker } from '@/components/CloudText';
import { PORTFOLIO, PORTFOLIO_FILTERS } from '@/data/content';
import { scrollToSection } from '@/lib/scroll';
import { Sparkles, Play, Globe } from 'lucide-react';
import { ProjectViewer } from './ProjectViewer';

function VideoTile({ item }) {
  const ref = useRef();
  const play = () => ref.current?.play().catch(() => {});
  const stop = () => { const v = ref.current; if (v) { v.pause(); v.currentTime = 0; } };
  const wide = !!item.link;
  return (
    <div className={`absolute inset-0 ${wide ? 'grid grid-cols-2' : ''}`} onPointerEnter={play} onPointerLeave={stop} data-testid={`portfolio-video-${item.id}`}>
      {wide && (
        <div className="relative flex items-center justify-center p-6 sm:p-10" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(255,190,60,0.22), rgba(3,12,36,0.3) 75%)' }}>
          <img src={item.image} alt={item.title} className="max-h-full w-auto rounded-3xl shadow-2xl ring-1 ring-white/20" style={{ filter: 'drop-shadow(0 20px 40px rgba(255,170,40,0.35))' }} />
        </div>
      )}
      <video ref={ref} muted loop playsInline preload="metadata" poster={item.video.poster} className="w-full h-full object-cover">
        {item.video.webm && <source src={item.video.webm} type="video/webm" />}
        <source src={item.video.mp4} type="video/mp4" />
      </video>
      <span className="absolute right-5 top-5 glass rounded-full px-3 py-1.5 font-display text-[0.58rem] tracking-[0.3em] uppercase flex items-center gap-1.5"><Play size={10} className="text-[#ffb37a]" /> Motion · hover to play</span>
    </div>
  );
}

function CloudFrame({ item, onOpen, index, span }) {
  const ref = useRef();
  const mx = useMotionValue(0), my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 16 }), sy = useSpring(my, { stiffness: 120, damping: 16 });
  const rx = useTransform(sy, [-0.5, 0.5], [7, -7]), ry = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const ix = useTransform(sx, [-0.5, 0.5], [-14, 14]), iy = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const onMove = (e) => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <Reveal delay={Math.min(index, 6) * 0.08} className={span || item.span}>
      <motion.button type="button" ref={ref} onPointerMove={onMove} onPointerLeave={reset} onClick={() => onOpen(item)}
        className="relative flex flex-col w-full h-full text-left group" style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} whileHover={{ y: -12 }} transition={{ type: 'spring', stiffness: 160, damping: 18 }}
        data-testid={`portfolio-item-${item.id}`} data-cursor="cloud" aria-label={`Open ${item.title}`}>
        <div className="absolute -inset-4 rounded-[40%] pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'radial-gradient(ellipse, rgba(255,255,255,0.35), rgba(255,255,255,0) 70%)', filter: 'blur(18px)' }} />
        <div className={`relative overflow-hidden h-full ${(item.portrait || item.photo) ? 'min-h-[420px]' : item.video ? 'min-h-[360px] sm:min-h-[460px]' : item.link ? 'min-h-[320px] sm:min-h-[560px]' : 'min-h-[260px]'} ${item.contain ? (index % 2 ? 'cloud-shape-2' : 'cloud-shape') : 'rounded-[2.5rem] sm:rounded-[3rem]'} glass`} style={{ boxShadow: '0 30px 80px rgba(3,12,36,0.45), inset 0 0 40px rgba(255,255,255,0.35)', background: item.contain ? `radial-gradient(ellipse at 50% 60%, ${item.tint}, rgba(255,255,255,0.06) 75%)` : item.portrait ? 'rgba(3,12,36,0.35)' : undefined }}>
          {item.video
            ? <VideoTile item={item} />
            : item.contain
            ? <motion.img src={item.image} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-8 sm:p-10 scale-100 group-hover:scale-[1.06] transition-transform duration-700" style={{ x: ix, y: iy, filter: 'drop-shadow(0 18px 30px rgba(6,26,61,0.45))' }} />
            : item.portrait
              ? <motion.img src={item.image} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-4 scale-100 group-hover:scale-[1.04] transition-transform duration-700" style={{ x: ix, y: iy, filter: 'drop-shadow(0 18px 30px rgba(6,26,61,0.5))' }} />
              : <motion.img src={item.image} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover scale-110 group-hover:scale-[1.16] transition-transform duration-700" style={{ x: ix, y: iy }} />}
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 60px 20px rgba(255,255,255,0.35)' }} />
          {item.link && !item.video && (
            <span className="absolute top-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#061a3d]/70 px-4 py-2 font-display text-[0.6rem] tracking-[0.3em] uppercase text-white ring-1 ring-white/30 backdrop-blur-md" data-testid={`portfolio-live-badge-${item.id}`}>
              <Globe size={12} /> Live site
            </span>
          )}
        </div>
        <div className="relative mt-4 pl-2">
          <p className="font-display text-[0.6rem] tracking-[0.35em] uppercase text-[#ffb37a]">{item.type}</p>
          <h3 className="font-display text-xl sm:text-2xl font-bold mt-1">{item.title}</h3>
        </div>
      </motion.button>
    </Reveal>
  );
}

export function PortfolioSection() {
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState('all');
  const active = PORTFOLIO_FILTERS.find((f) => f.id === filter);
  const items = PORTFOLIO.filter(active.match);
  return (
    <section id="portfolio" className="relative min-h-screen px-6 sm:px-[8vw] py-[18vh]" data-testid="portfolio-section">
      <AltitudeMarker index="04" label="PORTFOLIO" />
      <div className="relative max-w-6xl">
        <p className="cloud-eyebrow mb-5">Altitude 04 — Previous work</p>
        <CloudText lines={['FLOATING', 'THROUGH THE WORK']} testId="portfolio-title" />
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-3" role="tablist" aria-label="Filter work by type" data-testid="portfolio-filters">
            {PORTFOLIO_FILTERS.map((f) => {
              const on = f.id === filter;
              const count = PORTFOLIO.filter(f.match).length;
              return (
                <motion.button key={f.id} type="button" role="tab" aria-selected={on} onClick={() => setFilter(f.id)} whileHover={{ y: -3 }} data-testid={`portfolio-filter-${f.id}`} data-cursor="cloud"
                  className={`cloud-shape-2 flex items-center gap-2 px-5 py-3 font-display text-[0.68rem] tracking-[0.25em] uppercase transition-colors duration-300 ${on ? 'bg-[#f97316] text-white shadow-[0_0_30px_rgba(249,115,22,0.5)]' : 'glass text-white/75 hover:text-white'}`}>
                  {f.label} <span className={`text-[0.6rem] ${on ? 'text-white/85' : 'text-white/45'}`}>{count}</span>
                </motion.button>
              );
            })}
          </div>
        </Reveal>
        <div key={filter} className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-10 auto-rows-[minmax(300px,auto)]">
          {items.map((p, i) => <CloudFrame key={p.id} item={p} index={i} onOpen={setOpen} span={filter === 'all' ? undefined : p.link ? 'lg:col-span-8' : 'lg:col-span-4'} />)}
          <Reveal delay={0.2} className={filter === 'all' ? 'lg:col-span-12' : 'lg:col-span-4'}>
            <button type="button" onClick={() => scrollToSection('start')} className="glass relative flex h-full min-h-[300px] w-full flex-col items-start justify-end rounded-[2.5rem] sm:rounded-[3rem] p-8 text-left group overflow-hidden" data-testid="portfolio-your-brand-tile" data-cursor="cloud">
              <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.35), rgba(249,115,22,0) 70%)', filter: 'blur(18px)' }} />
              <p className="font-display text-[0.6rem] tracking-[0.35em] uppercase text-[#ffb37a]">Next in the clouds</p>
              <h3 className="cloud-text text-3xl sm:text-4xl mt-2">YOUR BRAND<br />HERE.</h3>
              <span className="btn-cloud primary mt-6"><Sparkles size={14} /> Start your project</span>
            </button>
          </Reveal>
        </div>
      </div>
      <AnimatePresence>{open && <ProjectViewer key={open.id} item={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </section>
  );
}
